/**
 * PWA Utility Functions
 * Handles service worker registration and PWA installation
 */

/**
 * Register service worker for offline capability
 */
export const registerServiceWorker = () => {
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;

      navigator.serviceWorker
        .register(swUrl)
        .then((registration) => {
          console.log('[PWA] Service Worker registered:', registration);
          
          // Check for updates periodically
          setInterval(() => {
            registration.update();
          }, 60 * 60 * 1000); // Check every hour

          // Handle updates
          registration.onupdatefound = () => {
            const installingWorker = registration.installing;
            if (installingWorker) {
              installingWorker.onstatechange = () => {
                if (installingWorker.state === 'installed') {
                  if (navigator.serviceWorker.controller) {
                    console.log('[PWA] New content available, please refresh.');
                    // Show update notification to user
                    showUpdateNotification();
                  } else {
                    console.log('[PWA] Content cached for offline use.');
                  }
                }
              };
            }
          };
        })
        .catch((error) => {
          console.error('[PWA] Service Worker registration failed:', error);
        });
    });
  }
};

/**
 * Unregister service worker
 */
export const unregisterServiceWorker = () => {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        console.error('[PWA] Service Worker unregistration failed:', error);
      });
  }
};

/**
 * Check if app is running as PWA
 */
export const isPWA = () => {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
};

/**
 * Check if PWA installation is available
 */
export const canInstallPWA = () => {
  return 'beforeinstallprompt' in window;
};

/**
 * Handle PWA installation
 */
let deferredPrompt = null;

export const setupPWAInstall = (callback) => {
  window.addEventListener('beforeinstallprompt', (e) => {
    // Prevent default prompt
    e.preventDefault();
    // Store the event
    deferredPrompt = e;
    // Notify callback that install is available
    if (callback) {
      callback(true);
    }
  });

  window.addEventListener('appinstalled', () => {
    console.log('[PWA] App installed successfully');
    deferredPrompt = null;
    if (callback) {
      callback(false);
    }
  });
};

/**
 * Trigger PWA installation prompt
 */
export const promptPWAInstall = async () => {
  if (!deferredPrompt) {
    console.log('[PWA] Install prompt not available');
    return false;
  }

  // Show the prompt
  deferredPrompt.prompt();

  // Wait for user response
  const { outcome } = await deferredPrompt.userChoice;
  console.log(`[PWA] User response: ${outcome}`);

  // Clear the prompt
  deferredPrompt = null;

  return outcome === 'accepted';
};

/**
 * Show update notification
 */
const showUpdateNotification = () => {
  // This can be customized to show a toast or modal
  if (window.confirm('New version available! Reload to update?')) {
    window.location.reload();
  }
};

/**
 * Check if device is online
 */
export const isOnline = () => {
  return navigator.onLine;
};

/**
 * Listen for online/offline status changes
 */
export const setupNetworkListener = (onlineCallback, offlineCallback) => {
  window.addEventListener('online', () => {
    console.log('[PWA] Network: Online');
    if (onlineCallback) onlineCallback();
  });

  window.addEventListener('offline', () => {
    console.log('[PWA] Network: Offline');
    if (offlineCallback) offlineCallback();
  });
};

export default {
  registerServiceWorker,
  unregisterServiceWorker,
  isPWA,
  canInstallPWA,
  setupPWAInstall,
  promptPWAInstall,
  isOnline,
  setupNetworkListener,
};
